import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authedFetch } from '../lib/supabase';
import { useAuth } from '../stores/auth';

/**
 * VOM permissions hook.
 *
 * Returns:
 *   { isVom, loading, assignments, aggregate, hasAny(feature) }
 *
 * - `assignments` is the raw list (one row per assigned org with merged permissions)
 * - `aggregate` is the union of grants across all assigned orgs (used to drive
 *   visibility of sidebar nav items — show if ANY org grants the feature)
 * - `hasAny(feature)` is a convenience predicate over the aggregate
 *
 * Non-VOM callers always get `isVom: false`, `loading: false`, empty arrays, and
 * `hasAny() === true` so this hook can be safely used to gate UI for any role.
 *
 * Freshness (Neon migration):
 *   Data lives in Neon Postgres, so there is no Supabase Realtime feed. For a
 *   VOM we poll /api/me/vom-permissions every 20s (React Query invalidation),
 *   which is how newly granted/revoked access shows up without a reload.
 *   Sidebar/BottomNav re-render automatically because they read this hook.
 */
export function useVomPermissions() {
  const { profile } = useAuth();
  const isVom = profile?.role === 'virtual_ops_manager';
  const vomUserId = profile?.id || null;
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: ['me-vom-permissions'],
    queryFn: () => authedFetch('/me/vom-permissions'),
    enabled: isVom,
    staleTime: 60_000,
  });

  // Assignments/permissions live in Neon (no Supabase Realtime). Poll every
  // 20s so a VOM picks up newly granted/revoked access without a reload.
  useEffect(() => {
    if (!isVom || !vomUserId) return undefined;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      queryClient.invalidateQueries({ queryKey: ['me-vom-permissions'] });
    }, 20_000);
    return () => clearInterval(id);
  }, [isVom, vomUserId, queryClient]);

  const assignments = q.data?.assignments || [];
  const aggregate = assignments.reduce((acc, a) => {
    const perms = a.permissions || {};
    for (const k of Object.keys(perms)) {
      acc[k] = acc[k] || !!perms[k];
    }
    return acc;
  }, {});

  return {
    isVom,
    loading: isVom && q.isLoading,
    assignments,
    aggregate,
    hasAny: (feature) => (isVom ? !!aggregate[feature] : true),
  };
}

export default useVomPermissions;
